class MindsightCartBundle extends HTMLElement {
  connectedCallback() {
    this.onCartUpdated = this.onCartUpdated.bind(this);
    document.addEventListener('cart:updated', this.onCartUpdated);

    this.addEventListener('click', (event) => {
      const addBtn = event.target.closest('[data-cart-bundle-add]');
      if (addBtn) {
        event.preventDefault();
        this.handleAdd(addBtn);
        return;
      }

      if (event.target.closest('[data-carousel-prev]')) {
        event.preventDefault();
        this.stepCarousel(-1);
      }

      if (event.target.closest('[data-carousel-next]')) {
        event.preventDefault();
        this.stepCarousel(1);
      }
    });

    this.initCarousel();

    if (this.dataset.autoApply === 'true' && this.dataset.discountCode) {
      this.tryAutoApply();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
  }

  onCartUpdated() {
    requestAnimationFrame(() => this.initCarousel());
  }

  initCarousel() {
    this.scroller = this.querySelector('[data-cart-bundle-scroller]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.counter = this.querySelector('[data-carousel-counter]');
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.currentIndex = 0;

    if (!this.slides.length) return;

    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.onScroll);
      this.onScroll = () => this.syncFromScroll();
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.scroller.scrollLeft = 0;
    }

    this.updateCarouselUI();
  }

  stepCarousel(delta) {
    if (!this.scroller || !this.slides.length) return;

    const width = this.scroller.clientWidth;
    if (!width) return;

    this.currentIndex = Math.max(0, Math.min(this.slides.length - 1, this.currentIndex + delta));
    this.scroller.scrollTo({ left: this.currentIndex * width, behavior: 'smooth' });
    this.updateCarouselUI();
  }

  syncFromScroll() {
    if (!this.scroller) return;

    const width = this.scroller.clientWidth;
    if (!width) return;

    this.currentIndex = Math.round(this.scroller.scrollLeft / width);
    this.updateCarouselUI();
  }

  updateCarouselUI() {
    if (this.counter) {
      this.counter.textContent = `${this.currentIndex + 1} / ${this.slides.length}`;
    }

    const hideNav = this.slides.length <= 1;
    if (this.prevBtn) this.prevBtn.hidden = hideNav;
    if (this.nextBtn) this.nextBtn.hidden = hideNav;
    if (this.counter) this.counter.hidden = hideNav;
  }

  getDiscountCodes() {
    const codes = [];
    document.querySelectorAll('.cart__discount[data-discount-code]').forEach((el) => {
      const code = el.dataset.discountCode;
      if (code) codes.push(code);
    });
    return codes;
  }

  isDiscountApplied(code) {
    return this.getDiscountCodes().includes(code);
  }

  async applyDiscountCode(discountCode) {
    if (!discountCode || this.isDiscountApplied(discountCode)) return;

    const discounts = this.getDiscountCodes();
    discounts.push(discountCode);

    const updateUrl = window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/update.js`
      : '/cart/update.js';

    const response = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: discounts.join(',') }),
    });

    if (!response.ok) throw new Error('Discount apply failed');
  }

  refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
  }

  async tryAutoApply() {
    const discountCode = this.dataset.discountCode;
    if (!discountCode || this.isDiscountApplied(discountCode)) return;

    try {
      await this.applyDiscountCode(discountCode);
      this.refreshCart();
    } catch (error) {
      console.error(error);
    }
  }

  async handleAdd(btn) {
    const variantId = btn.dataset.variantId;
    const discountCode = this.dataset.discountCode;
    if (!variantId || btn.disabled) return;

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const addUrl = window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart/add.js`
        : '/cart/add.js';

      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!addResponse.ok) throw new Error('Add to cart failed');

      if (discountCode) {
        await this.applyDiscountCode(discountCode);
      }

      btn.textContent = 'Added!';
      this.refreshCart();
    } catch (error) {
      console.error(error);
      btn.textContent = 'Try again';
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1500);
    }
  }
}

if (!customElements.get('mindsight-cart-bundle')) {
  customElements.define('mindsight-cart-bundle', MindsightCartBundle);
}
